/**
 * Hover Intent Utility
 *
 * Provides smart hover detection for mega menus using either:
 * - Simple delay-based approach
 * - Amazon-style triangular safe zone (tracks mouse movement toward menu)
 *
 * Usage:
 * const intent = createHoverIntent({
 *     mode: 'triangle',        // 'delay' or 'triangle'
 *     delay: 80,               // ms delay for switching (used in both modes)
 *     tolerance: 50,           // px tolerance for triangle detection
 *     debug: false             // show visual debug overlay
 * });
 *
 * // In your menu component:
 * intent.activate(menuElement);           // Start tracking for a menu
 * intent.deactivate();                    // Stop tracking
 * intent.shouldAllowSwitch(newMenuId);    // Returns true if switch is allowed
 */

export function createHoverIntent(options = {}) {
  const config = {
    mode: options.mode || 'triangle',
    delay: options.delay || 80,
    tolerance: options.tolerance || 50,
    overrideDelay: options.overrideDelay || 400, // ms before triangle block is overridden
    debug: options.debug || false,
  };

  let activeMenuRect = null;
  let lastMousePos = { x: 0, y: 0 };
  let prevMousePos = { x: 0, y: 0 };
  let tracking = false;
  let debugOverlay = null;
  let hoverStartTime = null; // Track when hover started on a new item

  /**
   * Check if a point is inside a triangle using cross product method
   */
  function pointInTriangle(px, py, x1, y1, x2, y2, x3, y3) {
    const d1 = sign(px, py, x1, y1, x2, y2);
    const d2 = sign(px, py, x2, y2, x3, y3);
    const d3 = sign(px, py, x3, y3, x1, y1);

    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;

    return !(hasNeg && hasPos);
  }

  function sign(px, py, x1, y1, x2, y2) {
    return (px - x2) * (y1 - y2) - (x1 - x2) * (py - y2);
  }

  /**
   * Handle mouse movement
   */
  function onMouseMove(e) {
    prevMousePos = { ...lastMousePos };
    lastMousePos = { x: e.clientX, y: e.clientY };

    if (config.debug && activeMenuRect) {
      updateDebugOverlay();
    }
  }

  /**
   * Check if mouse is moving toward the active menu (inside triangle)
   */
  function isMovingTowardMenu() {
    if (!activeMenuRect) return false;

    const { x, y } = lastMousePos;

    // Triangle vertices: current mouse position to menu corners
    // We use the corners on the side closest to the nav items (top of menu)
    const menuTop = activeMenuRect.top;
    const menuLeft = activeMenuRect.left - config.tolerance;
    const menuRight = activeMenuRect.right + config.tolerance;
    const menuBottom = activeMenuRect.bottom;

    // Create triangle from previous mouse position to menu edges
    // This creates a "safe corridor" toward the menu
    const inTriangle = pointInTriangle(
      x,
      y,
      prevMousePos.x,
      prevMousePos.y, // Apex at previous mouse position
      menuLeft,
      menuTop, // Top-left of menu
      menuRight,
      menuTop, // Top-right of menu
    );

    // Also check if mouse is directly over the menu area
    const overMenu =
      x >= menuLeft && x <= menuRight && y >= menuTop && y <= menuBottom;

    return inTriangle || overMenu;
  }

  /**
   * Create/update debug overlay showing the triangle
   */
  function updateDebugOverlay() {
    if (!debugOverlay) {
      debugOverlay = document.createElement('div');
      debugOverlay.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999;';
      document.body.appendChild(debugOverlay);
    }

    if (!activeMenuRect) {
      debugOverlay.innerHTML = '';
      return;
    }

    const { x, y } = lastMousePos;
    const menuLeft = activeMenuRect.left - config.tolerance;
    const menuRight = activeMenuRect.right + config.tolerance;
    const menuTop = activeMenuRect.top;

    const isInside = isMovingTowardMenu();
    const color = isInside ? 'rgba(0,255,0,0.3)' : 'rgba(255,0,0,0.3)';

    debugOverlay.innerHTML = `
            <svg width="100%" height="100%" style="position:absolute;top:0;left:0;">
                <polygon
                    points="${prevMousePos.x},${prevMousePos.y} ${menuLeft},${menuTop} ${menuRight},${menuTop}"
                    fill="${color}"
                    stroke="${isInside ? 'green' : 'red'}"
                    stroke-width="2"
                />
                <circle cx="${x}" cy="${y}" r="5" fill="blue"/>
                <circle cx="${prevMousePos.x}" cy="${prevMousePos.y}" r="3" fill="orange"/>
            </svg>
        `;
  }

  /**
   * Start tracking mouse for a specific menu
   */
  function activate(menuElement) {
    if (!menuElement) return;

    activeMenuRect = menuElement.getBoundingClientRect();

    if (!tracking) {
      document.addEventListener('mousemove', onMouseMove);
      tracking = true;
    }

    if (config.debug) {
      console.log('[HoverIntent] Activated for menu', activeMenuRect);
    }
  }

  /**
   * Stop tracking
   */
  function deactivate() {
    activeMenuRect = null;

    if (tracking) {
      document.removeEventListener('mousemove', onMouseMove);
      tracking = false;
    }

    if (debugOverlay) {
      debugOverlay.remove();
      debugOverlay = null;
    }

    if (config.debug) {
      console.log('[HoverIntent] Deactivated');
    }
  }

  /**
   * Update the menu rect (call when menu changes)
   */
  function updateMenuRect(menuElement) {
    if (menuElement) {
      activeMenuRect = menuElement.getBoundingClientRect();
    }
  }

  /**
   * Mark the start of hovering over a new item
   */
  function startHover() {
    hoverStartTime = Date.now();
    if (config.debug) {
      console.log('[HoverIntent] Hover started');
    }
  }

  /**
   * Check if hover has exceeded override delay
   */
  function hasExceededOverrideDelay() {
    if (!hoverStartTime) return false;
    return Date.now() - hoverStartTime >= config.overrideDelay;
  }

  /**
   * Check if we should allow switching to a new menu
   * Returns true if switch should happen, false if blocked by hover intent
   */
  function shouldAllowSwitch() {
    if (config.mode === 'delay') {
      // Delay mode doesn't block, it just delays (handled externally)
      return true;
    }

    // Override: if hovering long enough, always allow switch
    if (hasExceededOverrideDelay()) {
      if (config.debug) {
        console.log(
          '[HoverIntent] Override: hover exceeded',
          config.overrideDelay,
          'ms',
        );
      }
      return true;
    }

    // Triangle mode: block if moving toward current menu
    const movingToward = isMovingTowardMenu();

    if (config.debug) {
      console.log(
        '[HoverIntent] shouldAllowSwitch:',
        !movingToward,
        '(moving toward menu:',
        movingToward,
        ')',
      );
    }

    return !movingToward;
  }

  /**
   * Get current config
   */
  function getConfig() {
    return { ...config };
  }

  /**
   * Update config at runtime
   */
  function setConfig(newOptions) {
    Object.assign(config, newOptions);
  }

  return {
    activate,
    deactivate,
    updateMenuRect,
    shouldAllowSwitch,
    isMovingTowardMenu,
    startHover,
    getConfig,
    setConfig,
  };
}
